import { useCallback, useMemo } from 'react'
import { Block, Text, BLOCKS, INLINES } from '@contentful/rich-text-types'
import TabGroup, { TabItem } from '@/components/tab-group'
import Code from './code/code'
import { isPrint } from '@/utils/print'
import Callout from './callout'
import RichText from './rich-text'
import Image from './image.tsx'
import { isTypeComponentCode, isTypeResourceImage, isTypeComponentCallout, TypeComponentTabs } from '@/types/ctf'
import { useInspectorProps } from '@/utils/live-preview.ts'

export type TabsProps = TypeComponentTabs<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  groupId: string
}

type Tab = {
  heading: Text[]
  content: any[]
}

export default function Tabs({ groupId, body }: TabsProps) {
  const pageInspectorProps = useInspectorProps({ entryId: groupId, restrictedTypes: ['pages'] })

  const tabs = useMemo(() => {
    const data: Tab[] = []
    body?.content?.forEach((node: Block) => {
      if (node.nodeType === BLOCKS.HEADING_1) {
        data.push({ heading: node.content as Text[], content: [] })
      } else data[data.length - 1].content.push(node)
    })
    return data
  }, [])

  // Just render h2 and h3 as normal headings instead of anchor links
  const richH2Renderer = useCallback((node: any, children: any) => <h2>{children}</h2>, [])
  const richH3Renderer = useCallback((node: any, children: any) => <h3>{children}</h3>, [])

  const renderTabContent = (tab: Tab, tabId: string) => {
    return tab.content.map((node, i) => {
      const key = `tab-${tabId}-${i}`
      if (node.nodeType === BLOCKS.EMBEDDED_ENTRY) {
        const entry = node.data?.target
        const { id } = entry?.sys ?? {}
        if (isTypeComponentCode<'WITHOUT_UNRESOLVABLE_LINKS', any>(entry)) {
          return <Code key={key} {...entry.fields} />
        }
        if (isTypeResourceImage<'WITHOUT_UNRESOLVABLE_LINKS', any>(entry)) {
          return <Image key={key} {...entry.fields} />
        }
        if (isTypeComponentCallout<'WITHOUT_UNRESOLVABLE_LINKS', any>(entry)) {
          return (
            <Callout key={key} contentful_id={id} {...entry.fields} className="group">
              <RichText h2={richH2Renderer} h3={richH3Renderer} body={entry.fields.body} />
            </Callout>
          )
        }
      }
      return <RichText key={key} h2={richH2Renderer} h3={richH3Renderer} body={node} />
    })
  }

  const getTabId = (tab: Tab) => {
    let tabId = ''
    tab.heading.map((heading) => {
      if (heading.value?.length > 0) {
        tabId = heading.value
      } else tabId = tabId + ' ' + heading.value
    })

    return tabId.trimStart().toLowerCase().replace(/\s+/g, '-')
  }

  if (isPrint()) {
    return (
      <div>
        {tabs.map((tab) => {
          const tabId = getTabId(tab)
          return (
            <div key={tabId}>
              <h2>⇒{tab.heading.map((heading) => heading.value)}</h2>
              {renderTabContent(tab, tabId)}
            </div>
          )
        })}
      </div>
    )
  } else
    return (
      <TabGroup {...pageInspectorProps({ fieldId: 'body' })}>
        {tabs.map((tab) => {
          const tabId = getTabId(tab)
          return (
            <TabItem
              key={tabId}
              tabId={`tab-${tabId.trimStart().toLocaleLowerCase()}-${groupId}`}
              buttonContent={
                <>
                  {tab.heading.map((heading, index) => {
                    if ((heading.nodeType as string) === INLINES.EMBEDDED_ENTRY) {
                      return <RichText key={index} h2={richH2Renderer} h3={richH3Renderer} body={heading} />
                    }
                    return heading.value
                  })}
                </>
              }
            >
              {renderTabContent(tab, tabId)}
            </TabItem>
          )
        })}
      </TabGroup>
    )
}
